import React, { useState, useEffect, useCallback } from 'react';
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  DateField,
  NumberField,
  UrlField,
  FunctionField,
  ChipField,
} from 'react-admin';
import ReactMarkdown from 'react-markdown';


const updateTypeChoices = [
  { id: 'CHECKIN', name: '✅ Чекин' },
  { id: 'START', name: '🚀 Старт' },
  { id: 'COMPLETION', name: '🎉 Завершение' },
  { id: 'FAILURE', name: '❌ Провал' },
  { id: 'STATUS_CHANGE', name: '🔄 Изменение статуса' },
  { id: 'MILESTONE', name: '🏆 Веха' },
  { id: 'MOTIVATION', name: '💪 Мотивация' },
  { id: 'STRUGGLE', name: '😰 Борьба' },
  { id: 'SUCCESS', name: '🎯 Успех' },
];

const TypeChip = ({ record }: any) => {
  if (!record || !record.type) return <span>-</span>;
  const updateType = updateTypeChoices.find(choice => choice.id === record.type);
  return <ChipField record={{ value: updateType?.name || record.type }} source="value" />;
};

const ContentWithMarkdown = ({ record }: any) => {
  if (!record?.content) {
    return <div style={{ padding: '16px', color: '#666' }}>Нет содержимого</div>;
  }

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      marginTop: '8px',
      maxHeight: '400px',
      overflowY: 'auto'
    }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e9ecef',
        backgroundColor: '#f1f3f4',
        borderRadius: '8px 8px 0 0',
        fontSize: '0.9em',
        fontWeight: 'bold',
        color: '#5f6368'
      }}>
        📝 Содержимое события
      </div>
      
      <div style={{
        padding: '16px',
        lineHeight: '1.6',
        fontSize: '14px'
      }}>
        <ReactMarkdown
          components={{
            code: ({ node, inline, children, ...props }: any) => {
              if (inline) {
                return (
                  <code
                    style={{
                      backgroundColor: '#f1f3f4',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '0.9em',
                      fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                );
              }
              return (
                <pre
                  style={{
                    backgroundColor: '#f8f8f8',
                    border: '1px solid #e1e4e8',
                    borderRadius: '6px',
                    padding: '16px',
                    overflow: 'auto',
                    fontSize: '14px',
                    lineHeight: '1.45',
                    fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                  }}
                >
                  <code {...props}>{children}</code>
                </pre>
              );
            },
            h1: ({ children }) => (
              <h1 style={{ color: '#1a73e8', borderBottom: '2px solid #e8f0fe', paddingBottom: '8px' }}>
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 style={{ color: '#1a73e8', borderBottom: '1px solid #e8f0fe', paddingBottom: '6px' }}>
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 style={{ color: '#1a73e8' }}>{children}</h3>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1a73e8', textDecoration: 'none' }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
              >
                {children}
              </a>
            ),
            blockquote: ({ children }) => (
              <blockquote
                style={{
                  borderLeft: '4px solid #1a73e8',
                  paddingLeft: '16px',
                  margin: '16px 0',
                  color: '#5f6368',
                  fontStyle: 'italic'
                }}
              >
                {children}
              </blockquote>
            ),
          }}
        >
          {record.content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

// Компонент для динамической загрузки комментариев
const CommentsLoader = ({ updateId, commentsCount }: { updateId: string, commentsCount: number }) => {
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadComments = useCallback(async () => {
    if (commentsCount === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Используем прямой fetch запрос
      
      // Используем прямой запрос к GraphQL
      const queryString = `
        query GetComments($updateId: ID!) {
          getComments(updateId: $updateId) {
            id
            content
            createdAt
            user {
              id
              name
              email
            }
          }
        }
      `;

      const response = await fetch('http://localhost:4000/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          query: queryString,
          variables: { updateId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      setComments(result.data.getComments || []);
    } catch (err: any) {
      console.error('Error loading comments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [updateId, commentsCount]);

  useEffect(() => {
    loadComments();
  }, [updateId, commentsCount, loadComments]);

  if (commentsCount === 0) {
    return <div style={{ padding: '16px', color: '#666' }}>Нет комментариев</div>;
  }

  if (loading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <div style={{ color: '#1a73e8', marginBottom: '8px' }}>🔄 Загрузка комментариев...</div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          Загружаем {commentsCount} комментариев
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ color: '#d32f2f', marginBottom: '12px', fontWeight: 'bold' }}>
          ❌ Ошибка загрузки комментариев
        </div>
        <div style={{ 
          color: '#666', 
          fontSize: '12px',
          backgroundColor: '#ffebee',
          padding: '12px',
          borderRadius: '4px',
          border: '1px solid #f44336'
        }}>
          <strong>Ошибка:</strong> {error}<br/>
          <strong>Update ID:</strong> {updateId}
        </div>
        <button 
          onClick={loadComments}
          style={{
            marginTop: '12px',
            padding: '8px 16px',
            backgroundColor: '#1a73e8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Повторить загрузку
        </button>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ color: '#f57c00', marginBottom: '8px' }}>
          ⚠️ Комментарии не найдены
        </div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          Ожидалось {commentsCount} комментариев, но получено 0
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <div style={{
        marginBottom: '16px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        color: '#1a73e8'
      }}>
        💬 Комментарии ({comments.length})
      </div>
      
      {comments.map((comment: any, index: number) => (
        <div key={comment.id} style={{
          marginBottom: '16px',
          padding: '16px',
          border: '1px solid #e1e4e8',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa'
        }}>
          {/* Заголовок комментария */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
            paddingBottom: '8px',
            borderBottom: '1px solid #e9ecef'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontWeight: 'bold', color: '#1a73e8' }}>
                👤 {comment.user?.name || 'Аноним'}
              </span>
              {comment.user?.email && (
                <span style={{ color: '#666', fontSize: '0.9em' }}>
                  ({comment.user.email})
                </span>
              )}
            </div>
            <span style={{ color: '#666', fontSize: '0.9em' }}>
              📅 {new Date(comment.createdAt).toLocaleString('ru-RU')}
            </span>
          </div>
          
          {/* Содержимое комментария с Markdown */}
          <div style={{
            lineHeight: '1.6',
            fontSize: '14px'
          }}>
            <ReactMarkdown
              components={{
                code: ({ node, inline, children, ...props }: any) => {
                  if (inline) {
                    return (
                      <code
                        style={{
                          backgroundColor: '#f1f3f4',
                          padding: '2px 4px',
                          borderRadius: '3px',
                          fontSize: '0.9em',
                          fontFamily: 'monospace'
                        }}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }
                  return (
                    <pre
                      style={{
                        backgroundColor: '#f8f8f8',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '12px',
                        overflow: 'auto',
                        fontSize: '13px',
                        fontFamily: 'monospace'
                      }}
                    >
                      <code {...props}>{children}</code>
                    </pre>
                  );
                },
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1a73e8' }}
                  >
                    {children}
                  </a>
                ),
                p: ({ children }) => <p style={{ margin: '8px 0' }}>{children}</p>,
              }}
            >
              {comment.content}
            </ReactMarkdown>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ChallengeUpdateShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Основная информация">
        <TextField source="id" label="ID" />
        <FunctionField 
          label="Тип события"
          render={(record: any) => <TypeChip record={record} />}
        />
        <NumberField source="dayNumber" label="День челленджа" />
        
        {/* Содержимое с Markdown */}
        <FunctionField 
          label="Содержимое"
          render={(record: any) => <ContentWithMarkdown record={record} />}
        />
        
        {/* Автор */}
        <TextField source="user.name" label="Автор" />
        <TextField source="user.email" label="Email автора" />
        
        {/* Челлендж */}
        <TextField source="challenge.title" label="Челлендж" />
        <TextField source="challenge.type" label="Тип челленджа" />
        <TextField source="challenge.status" label="Статус челленджа" />
        
        {/* Статистика */}
        <NumberField source="likesCount" label="Количество лайков" />
        <NumberField source="commentsCount" label="Количество комментариев" />
        
        <DateField source="createdAt" label="Дата создания" showTime />
      </Tab>

      <Tab label="Изображения">
        <UrlField source="imageUrl" label="Главное изображение" />
        <FunctionField 
          label="Все изображения"
          render={(record: any) => {
            if (!record.images || record.images.length === 0) {
              return <div style={{ padding: '16px', color: '#666' }}>Нет изображений</div>;
            }
            return (
              <div style={{ padding: '16px' }}>
                {record.images.map((imageUrl: string, index: number) => (
                  <div key={index} style={{ 
                    marginBottom: '16px',
                    padding: '12px',
                    border: '1px solid #e1e4e8',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                      📷 Изображение {index + 1}
                    </div>
                    <a 
                      href={imageUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#1a73e8', textDecoration: 'none' }}
                    >
                      {imageUrl}
                    </a>
                    <div style={{ marginTop: '8px' }}>
                      <img 
                        src={imageUrl} 
                        alt={`Изображение ${index + 1}`}
                        style={{ 
                          maxWidth: '200px', 
                          maxHeight: '200px',
                          borderRadius: '4px',
                          border: '1px solid #ddd'
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          }}
        />
      </Tab>

      <Tab label="Комментарии">
        <FunctionField 
          label="Комментарии к событию"
          render={(record: any) => (
            <CommentsLoader 
              updateId={record.id} 
              commentsCount={record.commentsCount || 0}
            />
          )}
        />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
